
import Vue, { PropType } from "vue";
import { getBrandConfig, loadBrandConfigs, isBrandConfigLoaded } from "@/services/brandConfigService";
import { Category } from "@/config/types";
import { getLauncherUrl, LOADING_PAGE_HTML } from "@/services/gameLauncher";
import OWLaunchModal from "@/components/OWLaunchModal.vue";
import { fetchGameFile } from "@/services/gameDataService";

const FALLBACK_THUMBNAIL = require('@/assets/thumbnails/coming_soon_default.png');

export default Vue.extend({
  name: "GamesGrid",
  components: { OWLaunchModal },
  props: {
    selectedCategory: {
      type: String,
      default: null,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    currentBrandId: {
      type: String,
      required: true,
    },
    brandFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    typeFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    integrationFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    launchingGameName: {
      type: String as PropType<string | null>,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      categoryGames: {} as Record<string, any[]>,
      copyState: {} as Record<string, "loading">,
      copyTimers: {} as Record<string, ReturnType<typeof setTimeout>>,
      toastVisible: false,
      toastMessage: "",
      toastTimer: null as ReturnType<typeof setTimeout> | null,

      showOWModal: false,
      pendingOWFile: null as any,
    };
  },
  created() {
    (this as any)._imgCache = Object.create(null) as Record<string, string>;
  },
  computed: {
    brandConfig() {
      try {
        return getBrandConfig(this.currentBrandId);
      } catch {
        return { categories: [], hideDisabled: false, demoOnly: false };
      }
    },
    isJogoBrand(): boolean {
      return this.currentBrandId === 'jogoglobal'
        || this.currentBrandId === 'jogoglobal2'
        || this.currentBrandId.includes('demo');
    },
    allGames(): any[] {
      const games: any[] = [];

      this.brandConfig.categories.forEach((category: Category) => {
        const categoryGames = this.categoryGames[category.name] || [];
        games.push(
          ...categoryGames.map((game: any) => ({
            ...game,
            category: category.name,
            gameType: game.gameType,
          }))
        );
      });

      return games;
    },
    games(): any[] {
      let filteredGames = this.allGames;

      if (this.selectedCategory) {
        filteredGames = filteredGames.filter(
          (game) => game.category === this.selectedCategory
        );
      }

      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const query = this.searchQuery.toLowerCase().trim();
        filteredGames = filteredGames.filter(
          (game) =>
            game.name.toLowerCase().includes(query) ||
            game.category.toLowerCase().includes(query)
        );
      }

      if (this.brandFilter) {
        filteredGames = filteredGames.filter(
          (g: any) => g.brand === this.brandFilter
        );
      }

      if (this.typeFilter) {
        filteredGames = filteredGames.filter(
          (g: any) => g.gameType === this.typeFilter
        );
      }

      if (this.integrationFilter) {
        filteredGames = filteredGames.filter(
          (g: any) => g.integration === this.integrationFilter
        );
      }

      if (this.brandConfig.demoOnly) {
        filteredGames = filteredGames.filter((game) => game.demo === true);
      }

      if (this.brandConfig.hideDisabled) {
        filteredGames = filteredGames.filter(
          (game) => !this.isGameDisabled(game)
        );
      }

      return filteredGames.slice().sort((a, b) => {
        if (this.isJogoBrand) {
          const aDisabled = this.isGameDisabled(a) ? 1 : 0;
          const bDisabled = this.isGameDisabled(b) ? 1 : 0;
          if (aDisabled !== bDisabled) return aDisabled - bDisabled;
        }
        return a.name.localeCompare(b.name);
      });
    },
  },
  watch: {
    currentBrandId: {
      immediate: true,
      handler() {
        this.loadGames();
      },
    },
  },
  methods: {
    async loadGames() {
      this.loading = true;
      this.categoryGames = {};
      if (!isBrandConfigLoaded(this.currentBrandId)) {
        await loadBrandConfigs();
      }
      const config = getBrandConfig(this.currentBrandId);

      await Promise.all(config.categories.map(async (category: Category) => {
        try {
          const raw = await fetchGameFile(category.gamesPath);
          let games: any[];
          if (Array.isArray(raw)) {
            games = raw;
          } else if (category.ipKeys) {
            games = category.ipKeys.flatMap((key) =>
              (raw[key] || []).map((g: any) => ({ ...g, brand: key }))
            );
          } else {
            games = Object.entries(raw).flatMap(([ip, ipGames]) =>
              (ipGames as any[]).map((g: any) => ({ ...g, brand: ip }))
            );
          }
          games = games.filter(
            (g: any) =>
              !g.accessOnly || g.accessOnly.includes(this.currentBrandId)
          );
          this.$set(this.categoryGames, category.name, games);
        } catch (e) {
          console.warn(`Games file not found: ${category.gamesPath}`);
          this.$set(this.categoryGames, category.name, []);
        }
      }));
      this.loading = false;
    },

    isGameDisabled(game: any): boolean {
      return !this.hasGameFile(game);
    },
    hasGameFile(game: any): boolean {
      return (game.gameFiles || []).some((f: any) => {
        if (f.actionType === 'api-launch') return !!(f.gameId || f.url);
        if (f.actionType === 'button') return !!f.url && f.action !== 'Download';
        return false;
      });
    },
    showCopyToast(message: string) {
      this.toastMessage = message;
      this.toastVisible = true;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
        this.toastTimer = null;
      }, 2000);
    },
    async copyGameLink(game: any): Promise<void> {
      if (this.copyState[game.name] === "loading") return;
      const file = (game.gameFiles || []).find(
        (f: any) =>
          (f.actionType === 'api-launch' && !!(f.gameId || f.url)) ||
          (f.actionType === 'button' && !!f.url && f.action !== 'Download')
      );
      if (!file) return;

      this.$set(this.copyState, game.name, "loading");
      try {
        let urlPromise: Promise<string>;
        if (file.actionType === "api-launch" && file.gameId) {
          urlPromise = getLauncherUrl(
            file.gameId,
            file.launchProvider,
            file.url
          );
        } else {
          urlPromise = Promise.resolve(file.url);
        }

        if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
          const blob = urlPromise.then(url => new Blob([url], { type: 'text/plain' }));
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
          } catch {
            const url = await urlPromise;
            await navigator.clipboard.writeText(url);
          }
        } else {
          const url = await urlPromise;
          await navigator.clipboard.writeText(url);
        }
        this.showCopyToast("Copied to clipboard");
      } catch (e: any) {
        this.showCopyToast(e?.message || "Failed to copy link");
      } finally {
        this.$delete(this.copyState, game.name);
      }
    },
    getImageSrc(imagePath: string): string {
      if (!imagePath) return FALLBACK_THUMBNAIL;
      if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
      const cache = (this as any)._imgCache as Record<string, string>;
      if (cache[imagePath]) return cache[imagePath];
      let src: string;
      try {
        src = require(`@/assets/${imagePath}`);
      } catch {
        src = FALLBACK_THUMBNAIL;
      }
      cache[imagePath] = src;
      return src;
    },
    getImageClass(imagePath: string): string {
      if (!imagePath) return 'contain-image';
      const containImages = [
        "coming_soon_default.png",
        "thumb-coming-soon.png",
        "coming-soon.png",
        "commingsoon",
        "placeholder.png",
      ];
      return containImages.some((img) => imagePath.includes(img)) ? "contain-image" : "";
    },

    async handleGameClick(game: any) {
      if (this.launchingGameName) return;

      if (!this.isJogoBrand) {
        this.$emit("game-clicked", game);
        return;
      }

      const file = (game.gameFiles || []).find(
        (f: any) => f.actionType === "api-launch"
      );
      if (!file) {
        // fallback for non-api-launch games
        this.$emit("game-clicked", game);
        return;
      }

      this.$emit("update:launchingGameName", game.name); // show spinner

      if (file.launchProvider === "oddswork") {
        this.pendingOWFile = file;
        this.showOWModal = true;
        this.$emit('update:launchingGameName', null);
        return;
      } else if (file.openDirectly || !file.gameId) {
        const directWin = window.open(file.url, "_blank");
        if (!directWin) this.showCopyToast("Please allow popups for this site to launch games.");
        this.$emit("update:launchingGameName", null);
      } else {
        const newWin = window.open('about:blank', '_blank');
        if (newWin) {
          newWin.document.write(LOADING_PAGE_HTML);
          newWin.document.close();
        }
        try {
          const url = await getLauncherUrl(
            file.gameId,
            file.launchProvider,
            file.url
          );
          if (newWin && !newWin.closed) {
            newWin.location.href = url;
          } else if (!newWin) {
            this.showCopyToast("Please allow popups for this site to launch games.");
          }
        } catch (err: any) {
          if (newWin && !newWin.closed) newWin.close();
          this.showCopyToast(err.message || "Launch failed");
        } finally {
          this.$emit("update:launchingGameName", null);
        }
      }
    },
    cancelOWLaunch() {
      this.showOWModal = false;
      this.pendingOWFile = null;
    },
  },

  mounted() {
    this.loadGames();
  },

  beforeDestroy() {
    Object.values(this.copyTimers).forEach(clearTimeout);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
});
